import { SelectionCircleBlueIcon, SelectionCircleIcon } from 'assets/icons';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import { AmountFilterOperation, REGEX } from 'constants/common';
import React, { FC } from 'react';
import { AmountFilter, AmountFilterItem, amountFilters } from './CustomAmountFilter';
import { VoidFn } from 'types/baseTypes';

interface AmountFilterPopoverPanelProps {
  operation: AmountFilterOperation;
  setOperation: (operation: AmountFilterOperation) => void;
  startValue: number;
  setStartValue: (startValue: number) => void;
  endValue: number;
  setEndValue: (endValue: number) => void;
  amountFilter: AmountFilter;
  setAmountFilter: (amountFilter: AmountFilter) => void;
  close: VoidFn;
  title?: string;
  isAmountFilter?: boolean;
}

const AmountFilterPopoverPanel: FC<AmountFilterPopoverPanelProps> = ({
  operation,
  setOperation,
  startValue,
  endValue,
  setStartValue,
  setEndValue,
  amountFilter,
  setAmountFilter,
  close,
  title,
  isAmountFilter = true
}) => {
  const { filterOperation, minAmount, maxAmount } = amountFilter ?? {};

  const handleOperationChange = value => {
    setOperation(value);
  };

  const handleStartValueChange = value => {
    if (REGEX.amount.test(value)) setStartValue(value);
  };

  const handleEndValueChange = value => {
    if (REGEX.amount.test(value)) setEndValue(value);
  };

  const isSelectedItem = (item: AmountFilterItem) => item.key === operation;

  const { prefix, placeholder } = isAmountFilter
    ? { prefix: '$ ', placeholder: '0.00' }
    : { prefix: '', placeholder: '0' };

  const renderFilterItem = (item: AmountFilterItem) => {
    const { key, Icon, value, SelectedIcon } = item ?? {};
    return (
      <div
        className={`flex flex-col gap-2  px-[30px]
      ${isSelectedItem(item) && key !== AmountFilterOperation.ALL && 'bg-secondaryBtn/50 pt-1.5 pb-2.5'}`}>
        <div className="flex cursor-pointer gap-4" role="presentation" onClick={() => handleOperationChange(key)}>
          {isSelectedItem(item) ? (
            <SelectionCircleBlueIcon className="shrink-0" />
          ) : (
            <SelectionCircleIcon className="shrink-0" />
          )}
          {isSelectedItem(item) ? <SelectedIcon className="shrink-0" /> : <Icon className="shrink-0" />}
          <div className="whitespace-nowrap text-sbase font-normal text-heading">{value}</div>
        </div>
        {isSelectedItem(item) && key !== AmountFilterOperation.ALL && (
          <div className="pl-8">
            {key === AmountFilterOperation.BW ? (
              <div className="flex gap-2.5">
                <div>
                  <div className="text-sbase font-semibold text-primaryText">Min</div>
                  <CustomInput
                    id="amount-filter-min-amount"
                    value={startValue ?? ''}
                    className="h-[30px] w-[128px] rounded-md border border-primaryBtn bg-white outline-none"
                    onChange={handleStartValueChange}
                    prefix={prefix}
                    prefixWrapperStyle="left-1.5"
                    placeholder={placeholder}
                  />
                </div>
                <div>
                  <div className="text-sbase font-semibold text-primaryText">Max</div>
                  <CustomInput
                    id="amount-filter-max-amount"
                    value={endValue ?? ''}
                    className="h-[30px] w-[128px] rounded-md border border-primaryBtn bg-white outline-none"
                    onChange={handleEndValueChange}
                    prefix={prefix}
                    prefixWrapperStyle="left-1.5"
                    placeholder={placeholder}
                  />
                </div>
              </div>
            ) : (
              <CustomInput
                id="amount-filter-min-amount"
                value={startValue ?? ''}
                className="h-[30px] w-[128px] rounded-md border border-primaryBtn bg-white outline-none"
                onChange={handleStartValueChange}
                prefix={prefix}
                prefixWrapperStyle="left-1.5"
                placeholder={placeholder}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const applyFilter = () => {
    let filter = {
      filterOperation: operation,
      minAmount: startValue,
      maxAmount: endValue
    };
    if (operation === AmountFilterOperation.ALL) {
      filter = {
        ...filter,
        minAmount: null,
        maxAmount: null
      };
    } else if (operation !== AmountFilterOperation.BW) {
      filter = {
        ...filter,
        maxAmount: null
      };
    }
    setAmountFilter(filter);
  };

  const applyBtnDisabled = () => {
    if (operation === filterOperation && startValue === minAmount && endValue === maxAmount) return true;
    switch (operation) {
      case AmountFilterOperation.EQ:
      case AmountFilterOperation.GT:
      case AmountFilterOperation.LT:
        if (startValue && REGEX.amount.test(startValue.toString())) return false;
        return true;
      case AmountFilterOperation.BW:
        if (
          startValue &&
          REGEX.amount.test(startValue.toString()) &&
          endValue &&
          REGEX.amount.test(endValue.toString()) &&
          +startValue < +endValue
        )
          return false;
        return true;
      case AmountFilterOperation.ALL:
        return false;
      default:
        return true;
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-4">
        <div className="pl-[30px] pr-4 text-sbase font-semibold  text-primaryText">
          Filter by {title?.toLocaleLowerCase() || 'amount'}
        </div>
        <div className="flex flex-col gap-[15px] ">
          {amountFilters.map((filter, index) => (
            <React.Fragment key={`${filter.key}-${index}`}>{renderFilterItem(filter)}</React.Fragment>
          ))}
        </div>
      </div>

      <div className="mt-2 flex w-full justify-end rounded-b-md bg-secondaryBtn px-4 py-2.5">
        <div onClick={close} className="cursor-pointer text-sbase font-semibold text-accentText">
          Cancel
        </div>
        <div
          id="amount-filter-apply-button"
          onClick={() => {
            if (applyBtnDisabled()) return;
            applyFilter();
            close();
          }}
          className={`ml-5 cursor-pointer text-sbase font-semibold text-secondary
                       ${applyBtnDisabled() && 'opacity-50'}`}>
          Apply
        </div>
      </div>
    </>
  );
};

export default AmountFilterPopoverPanel;
